/**
 * SearchInput — a labelled search field with the magnifier inside it.
 *
 * There were three copies of this around the app before this file, each
 * hand-positioning the same icon over the same rounded input. It is a
 * CONTROLLED input and nothing more: no debounce, no query, no results. Filter
 * timing belongs to the surface doing the filtering — a roster filtering rows
 * already in hand wants every keystroke, one hitting the network does not.
 */
import { Search } from 'lucide-react';
import { useId } from 'react';

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Read by screen readers; the visible field carries only the placeholder. */
  ariaLabel: string;
  /** Widths differ per surface, so the caller sizes it. */
  style?: React.CSSProperties;
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  ariaLabel,
  style,
}: SearchInputProps) {
  const id = useId();

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', ...style }}>
      <Search
        size={15}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 12,
          color: 'var(--nb-subtle)',
          pointerEvents: 'none',
        }}
      />
      <input
        id={id}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        style={{
          width: '100%',
          height: 34,
          // Room for the icon on the left; the native clear affordance on the right.
          padding: '0 12px 0 34px',
          borderRadius: 999,
          border: '1px solid var(--nb-border)',
          background: 'var(--nb-bg)',
          color: 'var(--nb-ink)',
          fontSize: 13,
          outlineOffset: 2,
        }}
      />
    </div>
  );
}
